import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, Copy, Download, RefreshCw, Wand2, FileText, 
  MessageSquare, Megaphone, Check, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { PartnerLayout } from '@/components/PartnerLayout';
import api from '@/lib/api';
import { toast } from 'sonner';

const contentTypes = [
  { id: 'description', label: 'Event Description', icon: FileText },
  { id: 'social', label: 'Social Media Post', icon: MessageSquare },
  { id: 'press', label: 'Press Release', icon: Megaphone },
];

const toneOptions = [
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual & Fun' },
  { value: 'luxury', label: 'Luxury & Exclusive' },
  { value: 'energetic', label: 'Energetic & Exciting' },
];

const AIMarketing = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isPartner, user, loading: authLoading, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [copiedSection, setCopiedSection] = useState(null);
  const [selectedType, setSelectedType] = useState('description');
  const [formData, setFormData] = useState({
    event_title: '',
    event_type: '',
    date: '',
    venue: '',
    additional_details: '',
    tone: 'professional'
  });

  useEffect(() => {
    const init = async () => {
      if (authLoading) return;
      
      if (!isAuthenticated) {
        navigate('/auth');
        return;
      }
      
      if (!user) {
        try {
          await refreshUser();
        } catch (error) {
          navigate('/auth');
          return;
        }
      }
      
      if (!isPartner) {
        navigate('/dashboard');
      }
    };
    
    init();
  }, [isAuthenticated, isPartner, authLoading, user]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/partner/ai-generate', {
        ...formData,
        content_type: selectedType
      });
      setGeneratedContent(response.data.content);
      toast.success('Content generated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate content');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, section) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(section);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const parseContent = (content) => {
    if (!content) return {};
    
    // Try to parse sections from the content
    const sections = {
      title: '',
      description: '',
      social: '',
      full: content
    };

    // Simple extraction (the AI usually returns structured content)
    const lines = content.split('\n');
    let currentSection = 'full';
    
    lines.forEach(line => {
      if (line.toLowerCase().includes('title:')) {
        sections.title = line.replace(/title:/i, '').trim();
      } else if (line.toLowerCase().includes('description:')) {
        currentSection = 'description';
      } else if (line.toLowerCase().includes('social')) {
        currentSection = 'social';
      }
    });

    return sections;
  };

  if (!isAuthenticated || !isPartner) return null;

  const parsedContent = parseContent(generatedContent);

  return (
    <PartnerLayout 
      title="AI Marketing Suite"
      subtitle="Generate professional marketing content in seconds"
      actions={
        generatedContent && (
          <Button variant="outline" onClick={() => setGeneratedContent(null)}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Start Over
          </Button>
        )
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Input Form */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-indigo-600" />
                Event Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleGenerate} className="space-y-5">
                {/* Content Type Selection */}
                <div className="space-y-2">
                  <Label>What do you want to generate?</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {contentTypes.map((type) => {
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => setSelectedType(type.id)}
                          className={`p-3 rounded-lg border-2 text-center transition-all ${
                            selectedType === type.id
                              ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-950'
                              : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                          }`}
                        >
                          <Icon className={`w-5 h-5 mx-auto mb-1 ${
                            selectedType === type.id ? 'text-indigo-600' : 'text-muted-foreground'
                          }`} />
                          <span className={`text-xs font-medium ${
                            selectedType === type.id ? 'text-indigo-600' : 'text-muted-foreground'
                          }`}>
                            {type.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="event_title">Event Title *</Label>
                    <Input
                      id="event_title"
                      value={formData.event_title}
                      onChange={(e) => setFormData({ ...formData, event_title: e.target.value })}
                      placeholder="Jazz Night at The Loft"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="event_type">Event Type *</Label>
                    <Input
                      id="event_type"
                      value={formData.event_type}
                      onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                      placeholder="Live Music"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date">Date *</Label>
                    <Input
                      id="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      placeholder="Friday, March 15th"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="venue">Venue *</Label>
                    <Input
                      id="venue"
                      value={formData.venue}
                      onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                      placeholder="The Loft RiNo"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tone">Tone</Label>
                  <Select 
                    value={formData.tone} 
                    onValueChange={(v) => setFormData({ ...formData, tone: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {toneOptions.map((tone) => (
                        <SelectItem key={tone.value} value={tone.value}>
                          {tone.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="additional_details">Additional Details</Label>
                  <Textarea
                    id="additional_details"
                    rows={3}
                    value={formData.additional_details}
                    onChange={(e) => setFormData({ ...formData, additional_details: e.target.value })}
                    placeholder="Special guests, themes, unique features, target audience..."
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Generating Magic...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Generate Content
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Tips Card */}
          <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/50 dark:to-purple-950/50 border-indigo-200 dark:border-indigo-800">
            <CardContent className="p-5">
              <h4 className="font-bold text-foreground mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-indigo-600" />
                Pro Tips for Better Results
              </h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-indigo-600">•</span>
                  Be specific about your event's unique features
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-600">•</span>
                  Mention any special guests or performers
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-600">•</span>
                  Include target audience for better tone matching
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Output Section */}
        <div className="space-y-6">
          {generatedContent ? (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-indigo-600" />
                    Generated Content
                  </CardTitle>
                  <Badge className="bg-green-100 text-green-700 border-0">
                    <Check className="w-3 h-3 mr-1" />
                    Ready to use
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Full Content */}
                <div className="relative">
                  <div className="absolute top-3 right-3 z-10">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => copyToClipboard(generatedContent, 'full')}
                      className="shadow-sm"
                    >
                      {copiedSection === 'full' ? (
                        <Check className="w-4 h-4 mr-1" />
                      ) : (
                        <Copy className="w-4 h-4 mr-1" />
                      )}
                      {copiedSection === 'full' ? 'Copied!' : 'Copy All'}
                    </Button>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border max-h-[500px] overflow-y-auto">
                    <pre className="text-sm text-foreground whitespace-pre-wrap font-sans leading-relaxed">
                      {generatedContent}
                    </pre>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      // Trigger re-generation with same data
                      handleGenerate({ preventDefault: () => {} });
                    }}
                    disabled={loading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Regenerate
                  </Button>
                  <Button
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                    onClick={() => {
                      copyToClipboard(generatedContent, 'full');
                    }}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy & Use
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed h-full min-h-[400px] flex items-center justify-center">
              <CardContent className="text-center py-16">
                <div className="w-20 h-20 rounded-full bg-indigo-100 dark:bg-indigo-950 flex items-center justify-center mx-auto mb-6">
                  <Sparkles className="w-10 h-10 text-indigo-600" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">
                  AI-Powered Marketing
                </h3>
                <p className="text-muted-foreground max-w-sm mx-auto">
                  Fill in your event details and let AI create compelling marketing content that converts
                </p>
              </CardContent>
            </Card>
          )}

          {/* Usage Stats */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-950 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">AI Credits</p>
                    <p className="text-sm text-muted-foreground">Unlimited on your plan</p>
                  </div>
                </div>
                <Badge className="bg-green-100 text-green-700 border-0">Active</Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </PartnerLayout>
  );
};

export default AIMarketing;
